import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import styles from 'components/panels/panel.module.scss'
import {
    createNaturalFeedIdleTaskQueue,
    type NaturalFeedIdleTaskQueue,
} from 'lib/naturalFeedIdleTasks'
import type { NaturalFeedSegment as NaturalFeedSegmentModel } from 'lib/naturalFeedSegments'
import {
    readNaturalFeedSegmentGeometry,
    rememberNaturalFeedSegmentGeometry,
} from 'lib/naturalFeedSession'
import { markNaturalFeedScrollActive, markNaturalFeedScrollIdle } from 'lib/naturalFeedActivity'
import {
    resolveNaturalFlowRootMargin,
    resolveNaturalFlowScrollDirection,
    type NaturalFlowScrollDirection,
} from 'lib/naturalFlowRootMargin'

const SEGMENT_RETENTION_ROOT_MARGIN = '5000px 0px 3000px 0px'
const SEGMENT_URGENT_ROOT_MARGIN = '1200px 0px 1200px 0px'
const HEIGHT_CHANGE_TOLERANCE_PX = 0.25
const GEOMETRY_PENDING_SELECTOR =
    '[data-natural-flow-geometry-pending="true"], [data-natural-feed-geometry-pending="true"]'

export type NaturalFlowGeometryStore = {
    read: (sessionKey: string, segmentKey: string, layoutSignature: string) => number | null
    remember: (
        sessionKey: string,
        segmentKey: string,
        layoutSignature: string,
        height: number,
    ) => void
}

type NaturalFeedSegmentProps<T> = {
    alwaysMounted: boolean
    estimatedHeight: number
    layoutSignature: string
    geometryStore: NaturalFlowGeometryStore
    mountInitially: boolean
    rememberedHeight: number | null
    renderRow: (row: T, index: number, taskQueue: NaturalFeedIdleTaskQueue) => React.ReactNode
    rowOffset: number
    retentionRootMargin: string
    segment: NaturalFeedSegmentModel<T>
    sessionKey: string
    surface: 'feed' | 'thread'
    suspended: boolean
    taskQueue: NaturalFeedIdleTaskQueue
    urgentRootMargin: string
}

function NaturalFeedSegment<T>({
    alwaysMounted,
    estimatedHeight,
    geometryStore,
    layoutSignature,
    mountInitially,
    rememberedHeight,
    renderRow,
    rowOffset,
    retentionRootMargin,
    segment,
    sessionKey,
    surface,
    suspended,
    taskQueue,
    urgentRootMargin,
}: NaturalFeedSegmentProps<T>) {
    const rootRef = useRef<HTMLDivElement | null>(null)
    const cancelMountRef = useRef<(() => void) | null>(null)
    const cancelMeasureRef = useRef<(() => void) | null>(null)
    const cancelUnmountRef = useRef<(() => void) | null>(null)
    const unmountRetryTimerRef = useRef<number | null>(null)
    const scheduleUnmountRef = useRef<() => void>(() => undefined)
    const initiallyMounted = alwaysMounted || (!suspended && mountInitially)
    const isInRetentionRangeRef = useRef(!suspended)
    const isMountedRef = useRef(initiallyMounted)
    const hasCommittedGeometryRef = useRef(rememberedHeight != null)
    const lastLayoutSignatureRef = useRef(layoutSignature)
    const [isMounted, setIsMounted] = useState(initiallyMounted)
    const [measuredHeight, setMeasuredHeight] = useState<number | null>(
        rememberedHeight ?? estimatedHeight,
    )
    const [geometryReady, setGeometryReady] = useState(rememberedHeight != null)
    const [lockCommittedHeight, setLockCommittedHeight] = useState(false)

    const taskKey = useCallback(
        (operation: 'mount' | 'measure' | 'unmount') => `${segment.key}:${operation}`,
        [segment.key],
    )
    const cancelMount = useCallback(() => {
        cancelMountRef.current?.()
        cancelMountRef.current = null
    }, [])
    const cancelMeasure = useCallback(() => {
        cancelMeasureRef.current?.()
        cancelMeasureRef.current = null
    }, [])
    const cancelUnmount = useCallback(() => {
        cancelUnmountRef.current?.()
        cancelUnmountRef.current = null
        if (unmountRetryTimerRef.current != null) {
            window.clearTimeout(unmountRetryTimerRef.current)
            unmountRetryTimerRef.current = null
        }
    }, [])

    const mountSegment = useCallback(() => {
        cancelMount()
        if (suspended || isMountedRef.current) return
        isMountedRef.current = true
        setGeometryReady(false)
        setLockCommittedHeight(hasCommittedGeometryRef.current)
        setIsMounted(true)
    }, [cancelMount, suspended])

    const scheduleMount = useCallback(() => {
        if (
            suspended ||
            isMountedRef.current ||
            (!alwaysMounted && !isInRetentionRangeRef.current)
        ) {
            return
        }
        cancelUnmount()
        cancelMount()
        cancelMountRef.current = taskQueue.enqueue(taskKey('mount'), 'mount', () => {
            cancelMountRef.current = null
            if (!suspended && (alwaysMounted || isInRetentionRangeRef.current)) mountSegment()
        })
    }, [alwaysMounted, cancelMount, cancelUnmount, mountSegment, suspended, taskKey, taskQueue])

    const scheduleUnmount = useCallback(() => {
        if (
            alwaysMounted ||
            !geometryReady ||
            measuredHeight == null ||
            isInRetentionRangeRef.current ||
            !isMountedRef.current
        ) {
            return
        }
        cancelMount()
        cancelUnmount()
        cancelUnmountRef.current = taskQueue.enqueue(taskKey('unmount'), 'unmount', () => {
            cancelUnmountRef.current = null
            if (rootRef.current?.querySelector(GEOMETRY_PENDING_SELECTOR)) {
                unmountRetryTimerRef.current = window.setTimeout(() => {
                    unmountRetryTimerRef.current = null
                    scheduleUnmountRef.current()
                }, 250)
                return
            }
            if (!isInRetentionRangeRef.current && isMountedRef.current) {
                isMountedRef.current = false
                setIsMounted(false)
            }
        })
    }, [
        alwaysMounted,
        cancelMount,
        cancelUnmount,
        geometryReady,
        measuredHeight,
        taskKey,
        taskQueue,
    ])
    scheduleUnmountRef.current = scheduleUnmount

    useLayoutEffect(() => {
        if (lastLayoutSignatureRef.current === layoutSignature) return

        lastLayoutSignatureRef.current = layoutSignature
        cancelUnmount()
        const nextRememberedHeight = geometryStore.read(sessionKey, segment.key, layoutSignature)
        if (
            !isMountedRef.current &&
            nextRememberedHeight == null &&
            hasCommittedGeometryRef.current
        ) {
            // Content changed while an exact spacer was far offscreen. Keep its last committed
            // outer height until the segment approaches, mounts under that lock, and measures
            // the new signature. Replacing it with a fresh estimate here would change total
            // scroll geometry without any visible content transaction.
            setGeometryReady(false)
            scheduleMount()
            return
        }
        hasCommittedGeometryRef.current = nextRememberedHeight != null
        setLockCommittedHeight(nextRememberedHeight != null && isMountedRef.current)
        setGeometryReady(nextRememberedHeight != null)
        setMeasuredHeight(nextRememberedHeight ?? estimatedHeight)
        if (!isMountedRef.current) {
            if (nextRememberedHeight == null) scheduleMount()
        }
    }, [
        cancelUnmount,
        estimatedHeight,
        geometryStore,
        layoutSignature,
        scheduleMount,
        segment.key,
        sessionKey,
    ])

    useEffect(() => {
        if (alwaysMounted) scheduleMount()
    }, [alwaysMounted, scheduleMount])

    useEffect(() => {
        if (!suspended || alwaysMounted) return
        isInRetentionRangeRef.current = false
        cancelMount()
        scheduleUnmount()
    }, [alwaysMounted, cancelMount, scheduleUnmount, suspended])

    useLayoutEffect(() => {
        if (!isMounted) return

        const element = rootRef.current
        if (!element) return

        const measure = () => {
            cancelMeasureRef.current = null
            const hasPendingContent = element.querySelector(GEOMETRY_PENDING_SELECTOR)
            if (hasPendingContent) {
                setGeometryReady(false)
                return
            }
            const nextHeight = element.getBoundingClientRect().height
            if (!Number.isFinite(nextHeight) || nextHeight <= 0) return

            setMeasuredHeight(current =>
                current == null || Math.abs(current - nextHeight) > HEIGHT_CHANGE_TOLERANCE_PX
                    ? nextHeight
                    : current,
            )
            geometryStore.remember(sessionKey, segment.key, layoutSignature, nextHeight)
            hasCommittedGeometryRef.current = true
            setLockCommittedHeight(false)
            setGeometryReady(true)
        }
        const scheduleMeasure = () => {
            // A ResizeObserver delivery means the committed geometry is stale immediately,
            // even if the actual measurement is deferred until scrolling becomes idle.
            setGeometryReady(false)
            if (!taskQueue.isScrolling()) {
                measure()
                return
            }
            cancelMeasure()
            cancelMeasureRef.current = taskQueue.enqueue(taskKey('measure'), 'measure', measure)
        }

        scheduleMeasure()
        if (typeof ResizeObserver === 'undefined') return

        const observer = new ResizeObserver(scheduleMeasure)
        observer.observe(element)
        return () => {
            observer.disconnect()
            cancelMeasure()
        }
    }, [
        cancelMeasure,
        geometryStore,
        isMounted,
        layoutSignature,
        segment.key,
        sessionKey,
        taskKey,
        taskQueue,
    ])

    useEffect(() => {
        const element = rootRef.current
        if (suspended || !element || typeof IntersectionObserver === 'undefined') return

        const root = element.closest('.scroll-container')
        const retentionObserver = new IntersectionObserver(
            entries => {
                const isInRetentionRange = entries.some(entry => entry.isIntersecting)
                isInRetentionRangeRef.current = isInRetentionRange
                if (isInRetentionRange) {
                    cancelUnmount()
                    scheduleMount()
                } else {
                    cancelMount()
                    scheduleUnmount()
                }
            },
            {
                root,
                rootMargin: retentionRootMargin,
                threshold: 0,
            },
        )
        const urgentObserver = new IntersectionObserver(
            entries => {
                if (!entries.some(entry => entry.isIntersecting)) return
                cancelUnmount()
                mountSegment()
            },
            {
                root,
                rootMargin: urgentRootMargin,
                threshold: 0,
            },
        )
        retentionObserver.observe(element)
        urgentObserver.observe(element)

        return () => {
            retentionObserver.disconnect()
            urgentObserver.disconnect()
        }
    }, [
        cancelMount,
        cancelUnmount,
        mountSegment,
        retentionRootMargin,
        scheduleMount,
        scheduleUnmount,
        suspended,
        urgentRootMargin,
    ])

    useEffect(() => {
        scheduleUnmount()
    }, [geometryReady, measuredHeight, scheduleUnmount])

    useEffect(
        () => () => {
            cancelMount()
            cancelMeasure()
            cancelUnmount()
        },
        [cancelMeasure, cancelMount, cancelUnmount],
    )

    // The segment re-renders for its own geometry state (measured height, readiness,
    // height lock) and whenever its parent does. None of that changes what the rows
    // render, so keep the same row elements and let React skip the rows.
    const renderedRows = useMemo(
        () =>
            isMounted
                ? segment.rows.map((row, index) => renderRow(row, rowOffset + index, taskQueue))
                : null,
        [isMounted, renderRow, rowOffset, segment.rows, taskQueue],
    )

    return (
        <div
            ref={rootRef}
            className={styles.naturalFeedSegment}
            data-natural-flow-segment={segment.key}
            data-natural-flow-geometry-ready={geometryReady ? 'true' : 'false'}
            data-natural-flow-segment-mounted={isMounted ? 'true' : 'false'}
            data-natural-flow-surface={surface}
            data-natural-feed-segment={surface === 'feed' ? segment.key : undefined}
            data-natural-feed-segment-mounted={
                surface === 'feed' ? (isMounted ? 'true' : 'false') : undefined
            }
            style={
                (!isMounted || lockCommittedHeight) && measuredHeight != null
                    ? { height: measuredHeight, overflow: 'hidden' }
                    : undefined
            }
        >
            {renderedRows}
        </div>
    )
}

type NaturalFlowSegmentsProps<T> = {
    backwardRootMargin?: string
    backwardPrefetchRootMargin?: string
    geometryStore: NaturalFlowGeometryStore
    forwardRootMargin?: string
    forwardPrefetchRootMargin?: string
    getEstimatedHeight: (segment: NaturalFeedSegmentModel<T>) => number
    getLayoutSignature: (segment: NaturalFeedSegmentModel<T>) => string
    isAlwaysMounted: (segment: NaturalFeedSegmentModel<T>) => boolean
    renderRow: (row: T, index: number, taskQueue: NaturalFeedIdleTaskQueue) => React.ReactNode
    segments: NaturalFeedSegmentModel<T>[]
    sessionKey: string
    initialMountSegmentCount?: number
    onPrefetchSegment?: (segment: NaturalFeedSegmentModel<T>) => void
    prefetchRootMargin?: string
    retentionRootMargin?: string
    surface: 'feed' | 'thread'
    suspended?: boolean
    urgentRootMargin?: string
}

export function NaturalFlowSegments<T>({
    backwardRootMargin,
    backwardPrefetchRootMargin,
    geometryStore,
    forwardRootMargin,
    forwardPrefetchRootMargin,
    getEstimatedHeight,
    getLayoutSignature,
    isAlwaysMounted,
    renderRow,
    segments,
    sessionKey,
    initialMountSegmentCount = 0,
    onPrefetchSegment,
    prefetchRootMargin,
    retentionRootMargin = SEGMENT_RETENTION_ROOT_MARGIN,
    surface,
    suspended = false,
    urgentRootMargin = SEGMENT_URGENT_ROOT_MARGIN,
}: NaturalFlowSegmentsProps<T>) {
    const rootRef = useRef<HTMLDivElement | null>(null)
    const lastScrollTopRef = useRef<number | null>(null)
    const scrollDirectionRef = useRef<NaturalFlowScrollDirection>(null)
    const [scrollDirection, setScrollDirection] = useState<NaturalFlowScrollDirection>(null)
    const scrollActivityTokenRef = useRef(Symbol('natural-feed-scroll'))
    const taskQueueRef = useRef<NaturalFeedIdleTaskQueue | null>(null)
    if (taskQueueRef.current == null) {
        taskQueueRef.current = createNaturalFeedIdleTaskQueue()
    }
    const taskQueue = taskQueueRef.current

    useLayoutEffect(() => {
        taskQueue.setSuspended(suspended)
    }, [suspended, taskQueue])

    const markScroll = useCallback(
        (event: Event) => {
            if (
                backwardRootMargin ||
                forwardRootMargin ||
                backwardPrefetchRootMargin ||
                forwardPrefetchRootMargin
            ) {
                const scrollTop = Number((event.currentTarget as HTMLElement | null)?.scrollTop)
                const previousScrollTop = lastScrollTopRef.current
                if (Number.isFinite(scrollTop)) {
                    lastScrollTopRef.current = scrollTop
                    const nextDirection = resolveNaturalFlowScrollDirection(
                        previousScrollTop,
                        scrollTop,
                    )
                    if (nextDirection != null && nextDirection !== scrollDirectionRef.current) {
                        scrollDirectionRef.current = nextDirection
                        setScrollDirection(nextDirection)
                    }
                }
            }
            markNaturalFeedScrollActive(scrollActivityTokenRef.current, event)
            taskQueue.markScroll(event)
        },
        [
            backwardPrefetchRootMargin,
            backwardRootMargin,
            forwardPrefetchRootMargin,
            forwardRootMargin,
            taskQueue,
        ],
    )
    const markIdle = useCallback(() => {
        markNaturalFeedScrollIdle(scrollActivityTokenRef.current)
        taskQueue.markIdle()
    }, [taskQueue])

    useLayoutEffect(() => {
        const scrollElement = rootRef.current?.closest('.scroll-container')
        if (!scrollElement) return

        lastScrollTopRef.current = scrollElement.scrollTop
        scrollElement.addEventListener('scroll', markScroll, { passive: true })
        scrollElement.addEventListener('wheel', markScroll, { passive: true })
        scrollElement.addEventListener('touchmove', markScroll, { passive: true })
        scrollElement.addEventListener('scrollend', markIdle, { passive: true })

        return () => {
            scrollElement.removeEventListener('scroll', markScroll)
            scrollElement.removeEventListener('wheel', markScroll)
            scrollElement.removeEventListener('touchmove', markScroll)
            scrollElement.removeEventListener('scrollend', markIdle)
            markNaturalFeedScrollIdle(scrollActivityTokenRef.current)
            taskQueue.dispose()
        }
    }, [markIdle, markScroll, taskQueue])

    const resolvedRetentionRootMargin = resolveNaturalFlowRootMargin({
        backwardRootMargin,
        direction: scrollDirection,
        fallbackRootMargin: retentionRootMargin,
        forwardRootMargin,
    })
    const resolvedUrgentRootMargin = resolveNaturalFlowRootMargin({
        backwardRootMargin,
        direction: scrollDirection,
        fallbackRootMargin: urgentRootMargin,
        forwardRootMargin,
    })
    const prefetchFallbackRootMargin =
        prefetchRootMargin || forwardPrefetchRootMargin || backwardPrefetchRootMargin
    const resolvedPrefetchRootMargin = prefetchFallbackRootMargin
        ? resolveNaturalFlowRootMargin({
              backwardRootMargin: backwardPrefetchRootMargin,
              direction: scrollDirection,
              fallbackRootMargin: prefetchFallbackRootMargin,
              forwardRootMargin: forwardPrefetchRootMargin,
          })
        : undefined

    useEffect(() => {
        const list = rootRef.current
        if (
            !list ||
            !onPrefetchSegment ||
            !resolvedPrefetchRootMargin ||
            typeof IntersectionObserver === 'undefined'
        ) {
            return
        }

        const segmentsByKey = new Map(segments.map(segment => [segment.key, segment]))
        const root = list.closest('.scroll-container')
        const prefetchObserver = new IntersectionObserver(
            entries => {
                entries.forEach(entry => {
                    if (!entry.isIntersecting) return
                    const segmentKey = (entry.target as HTMLElement).dataset.naturalFlowSegment
                    const segment = segmentKey ? segmentsByKey.get(segmentKey) : undefined
                    if (segment) onPrefetchSegment(segment)
                })
            },
            {
                root,
                rootMargin: resolvedPrefetchRootMargin,
                threshold: 0,
            },
        )
        list.querySelectorAll<HTMLElement>('[data-natural-flow-segment]').forEach(element =>
            prefetchObserver.observe(element),
        )

        return () => prefetchObserver.disconnect()
    }, [onPrefetchSegment, resolvedPrefetchRootMargin, segments])

    let rowOffset = 0

    return (
        <div
            ref={rootRef}
            className={styles.naturalFeedSegmentList}
            data-natural-flow={surface}
            data-natural-feed={surface === 'feed' ? 'true' : undefined}
        >
            {segments.map((segment, segmentIndex) => {
                const segmentRowOffset = rowOffset
                rowOffset += segment.rows.length
                const layoutSignature = getLayoutSignature(segment)
                const rememberedHeight = geometryStore.read(
                    sessionKey,
                    segment.key,
                    layoutSignature,
                )
                return (
                    <NaturalFeedSegment
                        key={segment.key}
                        alwaysMounted={isAlwaysMounted(segment)}
                        estimatedHeight={rememberedHeight ?? getEstimatedHeight(segment)}
                        geometryStore={geometryStore}
                        layoutSignature={layoutSignature}
                        mountInitially={segmentIndex < initialMountSegmentCount}
                        rememberedHeight={rememberedHeight}
                        renderRow={renderRow}
                        rowOffset={segmentRowOffset}
                        retentionRootMargin={resolvedRetentionRootMargin}
                        segment={segment}
                        sessionKey={sessionKey}
                        surface={surface}
                        suspended={suspended}
                        taskQueue={taskQueue}
                        urgentRootMargin={resolvedUrgentRootMargin}
                    />
                )
            })}
        </div>
    )
}

type NaturalFeedSegmentsProps<T> = Omit<
    NaturalFlowSegmentsProps<T>,
    'geometryStore' | 'initialMountSegmentCount' | 'surface'
>

const naturalFeedGeometryStore: NaturalFlowGeometryStore = {
    read: readNaturalFeedSegmentGeometry,
    remember: rememberNaturalFeedSegmentGeometry,
}

export default function NaturalFeedSegments<T>(props: NaturalFeedSegmentsProps<T>) {
    return (
        <NaturalFlowSegments
            {...props}
            geometryStore={naturalFeedGeometryStore}
            initialMountSegmentCount={0}
            surface="feed"
        />
    )
}
